import React, { useEffect, useState } from 'react';
import { Eye, Heart } from 'lucide-react';
import {
  listingActivityMessages,
  ACTIVITY_MESSAGE_DURATION_MS,
} from '../../utils/listingActivity';
import type { PropertyActivity } from '../../utils/listingActivity';
import { visibleAccent } from '../../utils/colourContrast';

interface ListingActivityOverlayProps {
  /** Result of fetchPropertyActivity — null hides the overlay entirely. */
  activity: PropertyActivity | null;
  themeColour1?: string;
  themeColour2?: string;
  /** Test hook: per-message display time. */
  messageDurationMs?: number;
}

/**
 * Argos-style social-proof card anchored bottom-left over the /listing gallery
 * image. Fades in, rotates once through the qualifying activity messages (the
 * tightest qualifying view window, e.g. "Viewed 12 times in the last hour",
 * then "Favourited by 20 users"), then fades out for good. Mounts only for
 * popular properties — message selection and gating live in the tested
 * listingActivityMessages presenter; this component just runs the rotation.
 */
const ListingActivityOverlay = ({
  activity,
  themeColour1,
  themeColour2,
  messageDurationMs = ACTIVITY_MESSAGE_DURATION_MS,
}: ListingActivityOverlayProps) => {
  const messages = listingActivityMessages(activity);
  const [messageIndex, setMessageIndex] = useState(0);
  const [finished, setFinished] = useState(false);

  // Property.tsx stays mounted across /listing/A -> /listing/B navigation, so
  // a new listing's activity must restart a finished (or mid-flight) rotation
  // rather than inheriting A's index/finished state.
  const messagesKey = messages.join("|");
  useEffect(() => {
    setMessageIndex(0);
    setFinished(false);
  }, [messagesKey]);

  useEffect(() => {
    if (messages.length === 0 || finished) return undefined;
    const timer = setTimeout(() => {
      if (messageIndex >= messages.length - 1) {
        setFinished(true);
      } else {
        setMessageIndex((index) => index + 1);
      }
    }, messageDurationMs);
    return () => clearTimeout(timer);
  }, [messageIndex, messages.length, finished, messageDurationMs]);

  if (messages.length === 0 || finished) return null;

  // Clamp: on the render where a shorter message list arrives, the reset
  // effect has not run yet and the stale index could fall off the end.
  const message = messages[Math.min(messageIndex, messages.length - 1)];
  const accent = visibleAccent(themeColour2 || '', themeColour1);
  const Icon = message.startsWith('Favourited') ? Heart : Eye;

  return (
    <div
      role="status"
      aria-live="polite"
      className="absolute bottom-4 left-4 z-40 pointer-events-none max-w-[calc(100%-2rem)]"
    >
      {/* Self-contained keyframes — no animation library in this repo (same
          inline-<style> pattern as FavoritesModal / Home). */}
      <style>
        {`
          @keyframes listing-activity-fade {
            0% { opacity: 0; transform: translateY(8px); }
            12% { opacity: 1; transform: translateY(0); }
            88% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(8px); }
          }
        `}
      </style>
      <div
        key={messageIndex}
        className="flex items-center gap-2.5 bg-white rounded-lg shadow-lg px-4 py-3"
        style={{
          borderLeft: `4px solid ${accent}`,
          animation: `listing-activity-fade ${messageDurationMs}ms ease-in-out both`,
        }}
      >
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: accent }} aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-900">{message}</span>
      </div>
    </div>
  );
};

export default ListingActivityOverlay;
